import { FC, Fragment } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Control, UseFormGetValues } from 'react-hook-form';
import { Column } from 'further-ui/components/Layout';
import { PeriodEndReportFormValues } from '../schema';
import { ToggleableFieldConfig } from './types';
import FieldGroup from './FieldGroup';
import ToggleableFieldRow from './ToggleableFieldRow';
import ReadOnlyDatapoint from './ReadOnlyDatapoint';

const fundMetricsFields: Array<ToggleableFieldConfig> = [
  {
    label: 'Period-end NAV bridge',
    valuePath: 'fundMetrics.fundValueBridge',
    includedPath: 'fundMetrics.fundValueBridge',
    tooltip:
      'Includes the fund’s net asset value bridge, showing how the NAV changed over the reporting period.',
  },
  {
    label: 'Fund-over-time bar chart',
    valuePath: 'fundMetrics.fundValueProgression',
    includedPath: 'fundMetrics.fundValueProgression',
    tooltip:
      'Includes a bar chart showing the fund NAV, paid-in capital and distributions over time.',
  },
  {
    label: 'Fund metrics breakdown',
    valuePath: 'fundMetrics.fundPerformanceMetrics',
    includedPath: 'fundMetrics.fundPerformanceMetrics',
    tooltip:
      'Includes a comprehensive table of fund metrics including fund size, net asset value, performance and other metrics.',
  },
  {
    label: 'Fund cash flow and IRR calculation',
    valuePath: 'fundMetrics.fundCashFlowAndIrr',
    includedPath: 'fundMetrics.fundCashFlowAndIrr',
    tooltip:
      'Includes a table demonstrating the fund cash flows and IRR since inception.',
  },
];

const OuterColumn = styled(Column)({
  maxWidth: '800px',
});

type Props = {
  control: Control<PeriodEndReportFormValues>;
  readOnly: boolean;
  getValues: UseFormGetValues<PeriodEndReportFormValues>;
};

const FundMetrics: FC<Props> = ({ control, readOnly, getValues }) => {
  return (
    <>
      {readOnly ? <Divider /> : null}
      <OuterColumn>
        {readOnly ? (
          <Column spacing="sm">
            <Typography variant="h5" color="grey.900">
              Fund metrics
            </Typography>
            {fundMetricsFields.map((config) => (
              <ReadOnlyDatapoint
                key={config.valuePath}
                label={config.label}
                value={getValues(config.valuePath)}
              />
            ))}
          </Column>
        ) : (
          <FieldGroup title="Fund metrics">
            {fundMetricsFields.map((config, index, array) => {
              const isNotLast = index < array.length - 1;

              return (
                <Fragment key={config.valuePath}>
                  <ToggleableFieldRow
                    config={config}
                    control={control}
                    noInput
                  />
                  {isNotLast ? <Divider /> : null}
                </Fragment>
              );
            })}
          </FieldGroup>
        )}
      </OuterColumn>
    </>
  );
};

export default FundMetrics;
